'use client';

import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Clock, Trash2, Plus, Sparkles, X, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useHorariosPorEmpresa } from '@/components/hooks/useHorarios';
import {
  DIAS_SEMANA,
  DIA_A_INDEX,
  DiaSemana,
} from '@/components/constants/dias';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  useTemporadas,
  formatearFechaCorta,
} from '@/components/hooks/useTemporadas';
import { TemporadaDialog } from '@/components/pages/dialogs/TemporadaDialog';
import { AplicarHorarioTrabajadoresDialog } from '@/components/pages/dialogs/AplicarHorarioTrabajadoresDialog';

const diasSemana = DIAS_SEMANA;

// Se puede usar de dos formas: con su propio boton (por defecto) o
// controlado desde fuera, para abrirlo desde otro dialogo ya posicionado
// en una temporada concreta.
export default function ScheduleDialog({
  id_empresa,
  className,
  open: openProp,
  onOpenChange,
  sinBoton = false,
  temporadaInicial,
}: any) {
  const controlado = openProp !== undefined;
  const [openInterno, setOpenInterno] = useState(false);
  const open = controlado ? openProp : openInterno;
  const setOpen = (valor: boolean) =>
    controlado ? onOpenChange?.(valor) : setOpenInterno(valor);

  const [idTemporada, setIdTemporada] = useState<string | null>(null);
  const [temporadaDialogOpen, setTemporadaDialogOpen] = useState(false);
  const [editandoTemporada, setEditandoTemporada] = useState(false);
  const [aplicarOpen, setAplicarOpen] = useState(false);

  const {
    temporadas,
    vigenteHoy,
    saving: savingTemporada,
    crearTemporada,
    editarTemporada,
    borrarTemporada,
  } = useTemporadas(id_empresa);

  const { horarios, guardarHorariosEmpresa, loadingFetch, saving } =
    useHorariosPorEmpresa(id_empresa, idTemporada);
  const [horariosPorDia, setHorariosPorDia] = useState<Record<number, any[]>>(
    {}
  );

  const temporadaActual =
    temporadas.find((t) => t.id_horario_temporada === idTemporada) ?? null;

  const esVigente =
    !!temporadaActual &&
    temporadaActual.id_horario_temporada === vigenteHoy?.id_horario_temporada;

  useEffect(() => {
    const inicial: Record<number, any[]> = {};
    diasSemana.forEach((_, i) => (inicial[i] = []));

    horarios.forEach((h) => {
      const diaNombre = h.dia_semana as DiaSemana;
      const idx = DIA_A_INDEX[diaNombre];
      if (!isNaN(idx) && idx >= 0 && idx < 7) {
        inicial[idx].push({
          id_horario: h.id_horario,
          dia_semana: idx,
          hora_inicio: h.hora_inicio,
          hora_fin: h.hora_fin,
        });
      }
    });
    setHorariosPorDia(inicial);
  }, [horarios, idTemporada]);

  // Los dos diálogos no conviven: se cierra el de horarios mientras se
  // gestiona la temporada y se vuelve a abrir al salir.
  const abrirNuevaTemporada = () => {
    setEditandoTemporada(false);
    setOpen(false);
    setTemporadaDialogOpen(true);
  };

  const abrirEditarTemporada = () => {
    setEditandoTemporada(true);
    setOpen(false);
    setTemporadaDialogOpen(true);
  };

  const cerrarTemporadaDialog = () => {
    setTemporadaDialogOpen(false);
    setOpen(true);
  };

  const handleCrearTemporada = async (form: any) => {
    const res = await crearTemporada(form);
    if (res.ok) setIdTemporada(res.idTemporada);
    return res;
  };

  const handleBorrarTemporada = async (id: string) => {
    const res = await borrarTemporada(id);
    if (res.ok) setIdTemporada(null);
    return res;
  };

  const handleOpen = () => setOpen(true);

  // Al abrirlo desde fuera, arranca en la temporada que pidan.
  const temporadaAplicada = useRef<string | null | false>(false);
  useEffect(() => {
    if (!open) {
      temporadaAplicada.current = false;
      return;
    }
    if (!controlado) return;
    const inicial = temporadaInicial ?? null;
    if (temporadaAplicada.current === inicial) return;
    setIdTemporada(inicial);
    temporadaAplicada.current = inicial;
  }, [open, controlado, temporadaInicial]);

  const actualizarCampo = (
    dia: number,
    idx: number,
    campo: string,
    valor: string
  ) => {
    setHorariosPorDia((prev) => {
      const copia = [...(prev[dia] || [])];
      copia[idx] = { ...copia[idx], [campo]: valor };
      return { ...prev, [dia]: copia };
    });
  };

  const agregarHorario = (dia: number) => {
    setHorariosPorDia((prev) => ({
      ...prev,
      [dia]: [
        ...(prev[dia] || []),
        { dia_semana: dia, hora_inicio: '', hora_fin: '' },
      ],
    }));
  };

  const eliminarHorario = (dia: number, idx: number) => {
    setHorariosPorDia((prev) => {
      const copia = [...(prev[dia] || [])];
      copia.splice(idx, 1);
      return { ...prev, [dia]: copia };
    });
  };

  const copiarHorarios = (desde: number, hacia: number) => {
    setHorariosPorDia((prev) => {
      const origen = prev[desde] || [];
      const nuevos = origen.map((h) => ({ ...h, dia_semana: hacia }));
      return { ...prev, [hacia]: nuevos };
    });
  };

  const validar = () => {
    const todas = Object.values(horariosPorDia).flat();
    for (const f of todas) {
      if (!f.hora_inicio || !f.hora_fin) {
        toast.error('Faltan horas de inicio o fin');
        return false;
      }
    }
    return true;
  };

  const guardar = async () => {
    if (!validar()) return;
    const { ok } = await guardarHorariosEmpresa(horariosPorDia);
    if (ok) {
      setOpen(false);
      window.dispatchEvent(new CustomEvent('horario-guardado'));
      if (Object.values(horariosPorDia).flat().length > 0) setAplicarOpen(true);
    }
  };

  return (
    <div className={className}>
      {!sinBoton && (
        <Button
          onClick={handleOpen}
          disabled={loadingFetch || saving}
          className="w-full rounded-lg text-white font-medium shadow-md hover:bg-[#0d2742] dark:hover:bg-[#0d2742] flex items-center justify-center gap-2 px-4 py-2 transition-colors"
          style={{ backgroundColor: '#103254' }}
        >
          <Clock className="w-4 h-4 mr-2" />
          {loadingFetch ? 'Cargando…' : 'Horario'}
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="
            z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
            [&>button:last-child]:hidden
            flex flex-col
            !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
            w-full max-w-none h-[85vh] rounded-t-2xl rounded-b-none
            data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
            sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
            sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:max-h-[92dvh] sm:rounded-xl sm:shadow-2xl
          "
        >
          {/* Header fijo */}
          <div className="px-4 sm:px-6 pt-5 pb-3 border-b border-gray-100 dark:border-white/10 shrink-0 relative">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-bold text-[#103254] dark:text-blue-400 pr-10">
                Horarios de la empresa
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500 dark:text-gray-400">
                Gestiona las franjas de apertura.
              </DialogDescription>
            </DialogHeader>
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scroll area */}
          <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 space-y-4 scrollbar-thin">
            <div className="flex items-center gap-2">
              <Select
                value={idTemporada ?? 'base'}
                onValueChange={(v) => {
                  if (v === 'nueva') return abrirNuevaTemporada();
                  setIdTemporada(v === 'base' ? null : v);
                }}
              >
                <SelectTrigger className="h-9 text-sm font-semibold flex-1 focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[70]">
                  <SelectItem value="base" className="text-sm">
                    Horario general
                  </SelectItem>
                  {temporadas.map((t) => (
                    <SelectItem
                      key={t.id_horario_temporada}
                      value={t.id_horario_temporada}
                      className="text-sm"
                    >
                      {t.nombre}
                      {t.id_horario_temporada ===
                      vigenteHoy?.id_horario_temporada
                        ? ' · en vigor'
                        : ''}
                    </SelectItem>
                  ))}
                  <SelectItem value="nueva" className="text-sm font-semibold">
                    + Nueva temporada
                  </SelectItem>
                </SelectContent>
              </Select>

              {temporadaActual && (
                <Button
                  variant="outline"
                  onClick={abrirEditarTemporada}
                  className="h-9 px-3 rounded-lg text-xs font-semibold text-[#103254] dark:text-blue-400 border-[#103254] dark:border-blue-400 shrink-0 flex items-center gap-1.5"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Editar
                </Button>
              )}
            </div>

            {temporadaActual && (
              <p className="text-[11px] text-gray-500 dark:text-gray-400 -mt-1 px-1">
                Del {formatearFechaCorta(temporadaActual.fecha_desde)} al{' '}
                {formatearFechaCorta(temporadaActual.fecha_hasta)}
                {esVigente ? ' (en vigor ahora mismo)' : ''}. Estas franjas
                sustituyen al horario general en esas fechas; los trabajadores
                sin horario propio de la temporada siguen con el suyo de
                siempre.
              </p>
            )}

            {!temporadaActual && vigenteHoy && (
              <p className="text-[11px] text-gray-500 dark:text-gray-400 -mt-1 px-1">
                Ahora mismo está en vigor{' '}
                <span className="font-semibold">{vigenteHoy.nombre}</span>, hasta
                el {formatearFechaCorta(vigenteHoy.fecha_hasta)}. Este horario
                general se aplicará a partir de esa fecha.
              </p>
            )}

            {temporadaActual?.cierre ? (
              <div className="text-center py-10 border-2 border-dashed border-gray-200 dark:border-white/10 rounded-xl">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  No se puede reservar en estas fechas
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Esta temporada está marcada como cerrada, así que no hay
                  horario que configurar.
                </p>
              </div>
            ) : (
              diasSemana.map((dia, d) => (
              <section
                key={d}
                className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl p-3 sm:p-4 shadow-sm"
              >
                {/* Cabecera del día */}
                <div className="flex justify-between items-center mb-3 gap-2">
                  <h3 className="font-bold text-[#103254] dark:text-blue-400 capitalize flex items-center gap-2 text-sm sm:text-base">
                    <div className="w-1 h-4 bg-blue-500 rounded-full shrink-0" />
                    {dia}
                  </h3>

                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      onClick={() => agregarHorario(d)}
                      className="rounded-lg text-white bg-[#103254] hover:bg-[#0d2742] h-8 px-2.5 text-[11px] font-semibold flex items-center transition-all active:scale-95 shadow-sm gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Añadir
                    </Button>

                    <Select
                      value=""
                      onValueChange={(v) => {
                        if (v) copiarHorarios(Number(v), d);
                      }}
                    >
                      <SelectTrigger className="h-8 text-[11px] font-semibold min-w-[90px] focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-gray-200">
                        <SelectValue placeholder="Copiar…" />
                      </SelectTrigger>
                      <SelectContent className="z-[70]">
                        {diasSemana.map((x, i) =>
                          i !== d ? (
                            <SelectItem
                              key={i}
                              value={String(i)}
                              className="text-sm"
                            >
                              {x}
                            </SelectItem>
                          ) : null
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Franjas horarias */}
                <div className="space-y-2">
                  {horariosPorDia[d]?.map((h, i) => (
                    <div key={i} className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/10 rounded-lg px-2 py-2">
                        {/* Label inicio/fin visibles en móvil */}
                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                          <div className="flex flex-col gap-1.5 flex-1 min-w-0 sm:flex-row sm:gap-2">
                            <div className="flex items-center gap-1.5 flex-1 min-w-0">
                              <span className="text-[10px] text-gray-400 font-medium w-7 shrink-0">
                                De
                              </span>
                              <Input
                                type="time"
                                value={h.hora_inicio}
                                onChange={(e) =>
                                  actualizarCampo(
                                    d,
                                    i,
                                    'hora_inicio',
                                    e.target.value
                                  )
                                }
                                className="h-9 text-sm rounded-md bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 focus:ring-1 focus:ring-blue-500 flex-1 min-w-0"
                              />
                            </div>
                            <div className="flex items-center gap-1.5 flex-1 min-w-0">
                              <span className="text-[10px] text-gray-400 font-medium w-7 shrink-0">
                                A
                              </span>
                              <Input
                                type="time"
                                value={h.hora_fin}
                                onChange={(e) =>
                                  actualizarCampo(
                                    d,
                                    i,
                                    'hora_fin',
                                    e.target.value
                                  )
                                }
                                className="h-9 text-sm rounded-md bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 focus:ring-1 focus:ring-blue-500 flex-1 min-w-0"
                              />
                            </div>
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => eliminarHorario(d, i)}
                          className="h-9 w-9 text-red-500 hover:bg-red-50 dark:hover:bg-red-950 shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  {/* Hint jornada partida — INLINE, no absolute */}
                  {horariosPorDia[d]?.length === 1 && (
                    <div className="flex items-center gap-1.5 px-1 pt-0.5">
                      <Sparkles className="w-3 h-3 text-orange-400 shrink-0" />
                      <p className="text-[11px] text-orange-500 font-medium">
                        ¿Jornada partida? Pulsa <strong>Añadir</strong> para
                        agregar otra franja.
                      </p>
                    </div>
                  )}

                  {(!horariosPorDia[d] || horariosPorDia[d].length === 0) && (
                    <div className="text-center py-2.5 border-2 border-dashed border-gray-100 dark:border-white/10 rounded-lg">
                      <p className="text-[11px] text-gray-400 italic">
                        Cerrado / Sin horario
                      </p>
                    </div>
                  )}
                </div>
                </section>
              ))
            )}
          </div>

          {/* Footer fijo */}
          <div className="px-4 sm:px-6 py-4 bg-white dark:bg-[#1c1c1e] border-t border-gray-100 dark:border-white/10 shrink-0">
            <DialogFooter>
              <Button
                onClick={guardar}
                disabled={loadingFetch || saving}
                className="w-full rounded-xl text-white h-11 bg-[#103254] hover:bg-[#0d2742] font-bold shadow-md transition-all active:scale-[0.98]"
              >
                {saving
                  ? 'Guardando...'
                  : temporadaActual
                    ? `Guardar ${temporadaActual.nombre}`
                    : 'Guardar Horarios'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <TemporadaDialog
        open={temporadaDialogOpen}
        onClose={cerrarTemporadaDialog}
        temporada={editandoTemporada ? temporadaActual : null}
        saving={savingTemporada}
        onCrear={handleCrearTemporada}
        onEditar={editarTemporada}
        onBorrar={handleBorrarTemporada}
      />

      <AplicarHorarioTrabajadoresDialog
        open={aplicarOpen}
        idEmpresa={id_empresa}
        idTemporada={idTemporada}
        nombreTemporada={temporadaActual?.nombre}
        onClose={() => setAplicarOpen(false)}
      />
    </div>
  );
}